import { useState } from 'react';
import { Bot, Cog, Shield, Check } from 'lucide-react';
import { toast } from 'sonner';

interface AgentAccess {
  id: string;
  name: string;
  description: string;
  roles: string[];
}

const AVAILABLE_ROLES = [
  { id: 'full_admin', label: 'Full Admin' },
  { id: 'director', label: 'Director' },
  { id: 'sales_manager', label: 'Sales Manager' },
  { id: 'operations_manager', label: 'Operations Manager' },
  { id: 'quality_manager', label: 'Quality Manager' },
];

export function AgentsSettingsPage() {
  const [agents, setAgents] = useState<AgentAccess[]>([
    {
      id: 'bookkeeper',
      name: 'Bookkeeper',
      description: 'AI bookkeeping assistant — QuickBooks, invoices, reconciliation',
      roles: ['full_admin'],
    },
  ]);

  function toggleRole(agentId: string, roleId: string) {
    setAgents((prev) =>
      prev.map((agent) => {
        if (agent.id !== agentId) return agent;
        const hasRole = agent.roles.includes(roleId);
        // full_admin always required
        if (roleId === 'full_admin' && hasRole) return agent;
        return {
          ...agent,
          roles: hasRole
            ? agent.roles.filter((r) => r !== roleId)
            : [...agent.roles, roleId],
        };
      }),
    );
  }

  function handleSave() {
    // TODO: persist to backend when API is ready
    toast.success('Agent access settings saved');
  }

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10">
          <Cog className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Agent Settings</h1>
          <p className="text-sm text-muted-foreground">
            Manage access control for AI agents
          </p>
        </div>
      </div>

      {/* Agent access cards */}
      <div className="space-y-4">
        {agents.map((agent) => (
          <div
            key={agent.id}
            className="border rounded-lg p-5 bg-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <Bot className="h-5 w-5 text-muted-foreground" />
              <div>
                <h2 className="font-medium">{agent.name}</h2>
                <p className="text-sm text-muted-foreground">
                  {agent.description}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Allowed Roles</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {AVAILABLE_ROLES.map((role) => {
                const isActive = agent.roles.includes(role.id);
                const isLocked = role.id === 'full_admin';
                return (
                  <button
                    key={role.id}
                    onClick={() => toggleRole(agent.id, role.id)}
                    disabled={isLocked}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                      isActive
                        ? 'bg-primary/10 border-primary/30 text-foreground'
                        : 'bg-background border-border text-muted-foreground hover:border-primary/20'
                    } ${isLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                        isActive
                          ? 'bg-primary border-primary'
                          : 'border-muted-foreground/30'
                      }`}
                    >
                      {isActive && (
                        <Check className="h-3 w-3 text-primary-foreground" />
                      )}
                    </div>
                    {role.label}
                    {isLocked && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        required
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Save */}
      <div className="mt-6">
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}
